import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useUserPlan, PLAN_CONFIG } from './useUserPlan';

export interface CreditTransaction {
  id: string;
  amount: number;
  action_type: string;
  description: string | null;
  created_at: string;
}

export interface CreditsState {
  creditsRemaining: number;
  creditsResetAt: string | null;
  freeEditsUsed: number;
  loading: boolean;
  recentTransactions: CreditTransaction[];
  pendingPlan: string | null;
}

export function useCredits() {
  const { user } = useAuth();
  const { plan, creditLimit } = useUserPlan();
  const [state, setState] = useState<CreditsState>({
    creditsRemaining: 0,
    creditsResetAt: null,
    freeEditsUsed: 0,
    loading: true,
    recentTransactions: [],
    pendingPlan: null,
  });

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('credits_remaining, credits_reset_at, free_edits_used, plan, pending_plan, topup_credits')
      .eq('id', user.id)
      .maybeSingle();

    if (!profile) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    let creditsRemaining = profile.credits_remaining ?? 0;
    const creditsResetAt = profile.credits_reset_at;
    const topupCredits = profile.topup_credits ?? 0;

    // Handle monthly reset if past due
    if (false && creditsResetAt && new Date(creditsResetAt) <= new Date()) {
      // Apply pending_plan if one was scheduled
      const activePlan = (profile.pending_plan ?? profile.plan ?? 'free') as keyof typeof PLAN_CONFIG;
      const newLimit = PLAN_CONFIG[activePlan]?.creditLimit ?? 10;
      const nextResetAt = new Date();
      nextResetAt.setMonth(nextResetAt.getMonth() + 1);
      nextResetAt.setDate(1);
      nextResetAt.setHours(0, 0, 0, 0);

      // Plan credits reset to plan limit; top-up credits roll over
      const newTotal = newLimit + topupCredits;

      await supabase
        .from('profiles')
        .update({
          plan: activePlan,
          credits_remaining: newTotal,
          credits_reset_at: nextResetAt.toISOString(),
          free_edits_used: 0,
          pending_plan: null,
          // topup_credits stays unchanged — it persists across resets
        })
        .eq('id', user.id);

      creditsRemaining = newTotal;

      const wasDowngrade = profile.pending_plan && profile.pending_plan !== profile.plan;

      await supabase.from('credit_transactions').insert({
        user_id: user.id,
        amount: newLimit,
        action_type: 'monthly_reset',
        description: wasDowngrade
          ? `Monthly reset — plan changed to ${activePlan} · ${newLimit} plan credits added (${topupCredits} top-up credits carried over)`
          : `Monthly reset — ${newLimit} plan credits added${topupCredits > 0 ? ` (${topupCredits} top-up credits carried over)` : ''}`,
      });
    }

    const { data: transactions } = await supabase
      .from('credit_transactions')
      .select('id, amount, action_type, description, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    setState({
      creditsRemaining,
      creditsResetAt: profile.credits_reset_at,
      freeEditsUsed: profile.free_edits_used ?? 0,
      loading: false,
      recentTransactions: (transactions ?? []) as CreditTransaction[],
      pendingPlan: profile.pending_plan ?? null,
    });
  }, [user, plan]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Real-time: re-fetch whenever credits_remaining changes on the profile row
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`credits-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          fetchCredits();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchCredits]);

  const hasEnoughCredits = useCallback((cost: number) => {
    return state.creditsRemaining >= cost;
  }, [state.creditsRemaining]);

  const creditPercent = creditLimit > 0
    ? Math.min((state.creditsRemaining / creditLimit) * 100, 100)
    : 100;

  const daysUntilReset = state.creditsResetAt
    ? Math.max(0, Math.ceil((new Date(state.creditsResetAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return {
    ...state,
    creditLimit,
    creditPercent,
    daysUntilReset,
    hasEnoughCredits,
    refetch: fetchCredits,
  };
}
